"use client";

import { cn } from "@/lib/utils";
import * as SheetPrimitive from "@radix-ui/react-dialog";
import { overlay } from "overlay-kit";
import * as React from "react";
import { useCallback, useMemo } from "react";
import { ErrorBoundaryWithToast } from "./error-boundary-with-toast";

const Sheet = SheetPrimitive.Root;

interface SheetContentProps extends React.ComponentPropsWithoutRef<typeof SheetPrimitive.Content> {}

export const FullScreenOverlay = React.forwardRef<React.ElementRef<typeof SheetPrimitive.Content>, SheetContentProps>(
  ({ className, children, ...props }, ref) => (
    <SheetPrimitive.Portal>
      <SheetPrimitive.Overlay
        className={cn(
          "fixed inset-0 z-[51] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
        )}
        ref={ref}
      />

      <SheetPrimitive.Content
        ref={ref}
        className={cn(
          "fixed z-[51] bg-background transition ease-in-out data-[state=closed]:duration-300 data-[state=open]:duration-500 data-[state=open]:animate-in data-[state=closed]:animate-out inset-x-0 top-0 bottom-0 border-t data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
          className,
        )}
        {...props}
      >
        <ErrorBoundaryWithToast>{children}</ErrorBoundaryWithToast>
      </SheetPrimitive.Content>
    </SheetPrimitive.Portal>
  ),
);

const SheetHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn("flex justify-between py-6 px-[10px] text-center border-b border-borderLight", className)}
    {...props}
  />
);
SheetHeader.displayName = "SheetHeader";

const SheetFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className)} {...props} />
);
SheetFooter.displayName = "SheetFooter";

const SheetTitle = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Title>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Title ref={ref} className={cn("text-sm font-semibold text-textPrimary", className)} {...props} />
));
SheetTitle.displayName = SheetPrimitive.Title.displayName;

interface FullScreenOverlayLayoutProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  children: React.ReactNode;
  leftAddon?: React.ReactNode;
  className?: string;
  withNoHeader?: boolean;
}

export function FullScreenOverlayLayout({
  open,
  onOpenChange,
  title,
  children,
  leftAddon,
  className,
  withNoHeader,
}: FullScreenOverlayLayoutProps) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <FullScreenOverlay className={className}>
        {withNoHeader ? null : (
          <SheetHeader>
            {leftAddon ?? <div className="w-4 p-[6px]"> </div>}
            <SheetTitle>{title}</SheetTitle>
            <FullScreenOverlayLayout.Close />
          </SheetHeader>
        )}
        {children}
      </FullScreenOverlay>
    </Sheet>
  );
}

FullScreenOverlayLayout.Close = function Close({ className }: { className?: string }) {
  return (
    <SheetPrimitive.Close className={cn("", className)}>
      <i className="inline-block i-bp-close size-4" />
      <span className="sr-only">Close</span>
    </SheetPrimitive.Close>
  );
};
export function useFullScreenOverlay() {
  let id: string | undefined;
  const open = useCallback(
    async (props: {
      title: string;
      children: React.ReactNode;
      className?: string;
      leftAddon?: React.ReactNode;
      withNoHeader?: boolean;
      onClose?: () => void;
    }) => {
      id = await overlay.open(({ isOpen, close }) => {
        return (
          <FullScreenOverlayLayout
            open={isOpen}
            onOpenChange={(isOpen) => {
              if (!isOpen) {
                props.onClose?.();
                close();
              }
            }}
            title={props.title}
            leftAddon={props.leftAddon}
            className={props.className}
            withNoHeader={props.withNoHeader}
          >
            {props.children}
          </FullScreenOverlayLayout>
        );
      });
    },
    [id],
  );

  return useMemo(() => ({ open, close: () => id && overlay.close(id) }), [open, id]);
}
